import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BulkUpdateTasks, PostProjectTaskTag } from '../../redux/taskSlice';
import { AddCircle24Filled } from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuDivider } from '@fluentui/react-components';
import { ChevronDown12Regular } from '@fluentui/react-icons';
import styles from './css/index.module.css';

const BulkTagUpdateMenu = ({ selectedRows, onBulkUpdateComplete, children, taskData }) => {
    const dispatch = useDispatch();
    const { projectTags } = useSelector(state => state.task);
    const [newTagName, setNewTagName] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [open, setOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [dataroomTags] = useState([]);

    // Extract all unique tags from taskData
    const getAllTagsFromTaskData = () => {
        // Handle both array and object structures
        let tasks = [];
        if (Array.isArray(taskData)) {
            tasks = taskData;
        } else if (taskData && taskData.data && Array.isArray(taskData.data)) {
            tasks = taskData.data;
        } else if (taskData && taskData.groups && Array.isArray(taskData.groups)) {
            // Handle grouped data - flatten all groups
            tasks = taskData.groups.flatMap(group => group.tasks || []);
        } else {
            return [];
        }

        const tagSet = new Set();
        tasks.forEach(task => {
            const taskTags = Array.isArray(task.tags)
                ? task.tags
                : task?.tag_repr
                ? [task.tag_repr]
                : [];
            taskTags.forEach(tag => {
                if (tag && tag.trim() !== '') {
                    tagSet.add(tag);
                }
            });
        });

        return Array.from(tagSet).map(tagName => ({
            id: `task_${tagName}`,
            name: tagName,
            source: 'task',
        }));
    };

    const handleBulkTagSelect = async tag => {
        if (!selectedRows || selectedRows.length === 0) return;

        setIsUpdating(true);
        try {
            // Update all selected tasks by ADDING the tag to their tags array.
            await dispatch(
                BulkUpdateTasks({
                    taskIds: selectedRows,
                    updates: { tags: [tag.name] },
                    suppressToast: true,
                }),
            );

            // Call the completion callback
            if (onBulkUpdateComplete) {
                onBulkUpdateComplete(tag, selectedRows.length);
            }

            setOpen(false);
        } catch (error) {
            console.error('Error updating bulk task tags:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleAddNewTag = async () => {
        if (!newTagName.trim()) return;

        setIsAdding(true);
        try {
            await dispatch(PostProjectTaskTag({ name: newTagName.trim() }));
            setNewTagName('');
            setOpen(false);
        } catch (error) {
            console.error('Error adding new tag:', error);
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <Menu open={open} onOpenChange={(e, data) => setOpen(data.open)}>
            <MenuTrigger>
                {children || (
                    <div className={styles.bulkUpdateMenu} style={{ opacity: isUpdating ? 0.6 : 1 }}>
                        Tag <ChevronDown12Regular className="ms-1" />
                    </div>
                )}
            </MenuTrigger>
            <MenuPopover style={{ width: '232px', maxHeight: '400px', overflowY: 'auto' }}>
                <MenuList>
                    {/* Dataroom Tags (when workstream is from dataroom) */}
                    {dataroomTags.length > 0 && (
                        <>
                            {dataroomTags.map(tag => (
                                <div
                                    key={tag.id}
                                    onClick={() => handleBulkTagSelect(tag)}
                                    className={styles.tagMenuItem}
                                    style={{ opacity: isUpdating ? 0.6 : 1, cursor: isUpdating ? 'not-allowed' : 'pointer' }}>
                                    <span>{tag.name}</span>
                                </div>
                            ))}
                        </>
                    )}

                    {/* Project Tags List (always show) */}
                    {projectTags && projectTags.length > 0 && (
                        <>
                            {projectTags.map(tag => (
                                <div
                                    key={tag.id}
                                    onClick={() => handleBulkTagSelect(tag)}
                                    className={styles.tagMenuItem}
                                    style={{ opacity: isUpdating ? 0.6 : 1, cursor: isUpdating ? 'not-allowed' : 'pointer' }}>
                                    <span>{tag.name}</span>
                                </div>
                            ))}
                        </>
                    )}

                    {/* All Tags from Task Data */}
                    {getAllTagsFromTaskData().length > 0 && (
                        <>
                            <div style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Task Tags</div>
                            {getAllTagsFromTaskData().map(tag => (
                                <div
                                    key={tag.id}
                                    onClick={() => handleBulkTagSelect(tag)}
                                    className={styles.tagMenuItem}
                                    style={{ opacity: isUpdating ? 0.6 : 1, cursor: isUpdating ? 'not-allowed' : 'pointer' }}>
                                    <span>{tag.name}</span>
                                </div>
                            ))}
                        </>
                    )}

                    {/* Add New Tag */}
                    <MenuDivider />
                    <div className="p-2">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <input
                                type="text"
                                placeholder="Add new tag"
                                value={newTagName}
                                onChange={e => setNewTagName(e.target.value)}
                                onKeyPress={e => e.key === 'Enter' && handleAddNewTag()}
                                className="form-control form-control-sm"
                                disabled={isAdding || isUpdating}
                            />
                            <button className="btn btn-sm btn-outline-primary" onClick={handleAddNewTag} disabled={!newTagName.trim() || isAdding || isUpdating}>
                                <AddCircle24Filled />
                            </button>
                        </div>
                    </div>
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

export default BulkTagUpdateMenu;
